// @ts-nocheck
/**
 * Sidebar main component.
 * QBO visual pattern: soft gray background, elongated right curves,
 * icon-only active/hover state, no Sign Out (dropdown handles that),
 * Customize in white footer box at the bottom.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bookmark, ChevronRight, LayoutDashboard, Pencil } from 'lucide-react';
import {
  ACCENT, FONT, DEFAULT, MUTED, SLIDE_IN,
  AsteriskIcon, CustomSlidersIcon,
  ALL_APPS, SLIM_ITEMS, SLIM_BOTTOM, REPORTS_ITEMS, DEFAULT_APP_ORDER,
} from './constants';
import CustomizeModal from './CustomizeModal';
import { CreateFlyout, FlyoutPanel, AllAppsFlyout, HoverPreviewCard, MobileDrawer, AppSubPanel, FlyServiceIcon } from './Flyouts';
import { FeedFlyout } from './FeedFlyout';

export default function Sidebar({ onLogout, mobileOpen, onMobileClose, isMobile }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [flyout,         setFlyout]        = useState(null);
  const [flyoutExpanded, setFlyoutExpanded]= useState(null);
  const [hovItem,        setHovItem]       = useState(null);
  const [preview,        setPreview]       = useState(null);
  const [localMobile,    setLocalMobile]   = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [showCustomize,  setShowCustomize] = useState(false);
  const [pinnedApps,     setPinnedApps]    = useState(['accounting']);
  const [appOrder,       setAppOrder]      = useState(DEFAULT_APP_ORDER);
  const [flyoutPinned,   setFlyoutPinned]  = useState(false);
  const [hoveredSubApp,  setHoveredSubApp]= useState(null);
  const [hoveredSubAppTop, setHoveredSubAppTop] = useState(null);
  const [moreAnchorTop, setMoreAnchorTop] = useState(null);
  const [feedAnchorTop, setFeedAnchorTop] = useState(null);
  const [reportsAnchorTop, setReportsAnchorTop] = useState(null);
  const [hoveredReportsGroup, setHoveredReportsGroup] = useState(null);
  const [hoveredReportsGroupTop, setHoveredReportsGroupTop] = useState(null);
  const [toast,          setToast]         = useState(false);

  const sidebarRef  = useRef(null);
  const hoverTimers = useRef({});
  const closeTimers = useRef({});

  const mobile = isMobile !== undefined ? isMobile : localMobile;

  useEffect(function() {
    const h = function() { setLocalMobile(window.innerWidth < 768); };
    window.addEventListener('resize', h);
    return function() { window.removeEventListener('resize', h); };
  }, []);

  useEffect(function() {
    // Click anywhere outside the sidebar + its flyouts closes every open panel,
    // even ones that were pinned open by a click (previously stuck open until X).
    const handler = function(e) {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setFlyout(null);
        setFlyoutExpanded(null);
        setFlyoutPinned(false);
        setPreview(null);
        setHoveredSubApp(null);
        setHoveredReportsGroup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, []);

  useEffect(function() {
    const handler = function(e) {
      if (e.key === 'Escape') {
        setFlyout(null);
        setShowCustomize(false);
        setFlyoutPinned(false);
      }
    };
    document.addEventListener('keydown', handler);
    return function() { document.removeEventListener('keydown', handler); };
  }, []);

  const goTo = function(path) {
    navigate(path);
    setFlyout(null);
    setFlyoutExpanded(null);
    setPreview(null);
    setFlyoutPinned(false);
    if (onMobileClose) onMobileClose();
  };

  const startOpen = function(id, delay) {
    clearTimeout(closeTimers.current[id]);
    hoverTimers.current[id] = setTimeout(function() {
      if (!flyoutPinned) setFlyout(id);
    }, delay !== undefined ? delay : 300);
  };

  const startClose = function(id) {
    clearTimeout(hoverTimers.current[id]);
    if (!flyoutPinned) {
      closeTimers.current[id] = setTimeout(function() {
        setFlyout(function(prev) { return prev === id ? null : prev; });
      }, 200);
    }
  };

  const onPanelEnter = function(id) {
    clearTimeout(closeTimers.current[id]);
  };

  const onPanelLeave = function(id) {
    if (!flyoutPinned) {
      closeTimers.current[id] = setTimeout(function() {
        setFlyout(function(prev) { return prev === id ? null : prev; });
      }, 200);
    }
  };

  const handleItemClick = function(item) {
    Object.values(hoverTimers.current).forEach(clearTimeout);
    setPreview(null);
    if (item.path) {
      goTo(item.path);
    } else if (item.action === 'customize') {
      setShowCustomize(true);
    } else if (item.flyout) {
      if (flyout === item.flyout && flyoutPinned) {
        setFlyout(null);
        setFlyoutPinned(false);
        setFlyoutExpanded(null);
      } else {
        setFlyout(item.flyout);
        setFlyoutPinned(true);
        setFlyoutExpanded(item.flyout === 'apps' ? 'accounting' : null);
      }
    }
  };

  const handleAccountingClick = function() {
    Object.values(hoverTimers.current).forEach(clearTimeout);
    setPreview(null);
    setFlyout('apps');
    setFlyoutExpanded('accounting');
    setFlyoutPinned(true);
  };

  const startPreview = function(app, e) {
    clearTimeout(closeTimers.current['preview']);
    const rect = e.currentTarget.getBoundingClientRect();
    hoverTimers.current['preview'] = setTimeout(function() {
      setPreview({ app: app, top: rect.top });
    }, 300);
  };

  const endPreview = function() {
    clearTimeout(hoverTimers.current['preview']);
    closeTimers.current['preview'] = setTimeout(function() { setPreview(null); }, 200);
  };

  const keepPreview = function() {
    clearTimeout(closeTimers.current['preview']);
  };

  const openSubPanel = function(appId, top) {
    clearTimeout(closeTimers.current['subpanel']);
    setHoveredSubApp(appId);
    if (typeof top === 'number') setHoveredSubAppTop(top);
  };

  const closeSubPanelSoon = function() {
    closeTimers.current['subpanel'] = setTimeout(function() { setHoveredSubApp(null); setHoveredReportsGroup(null); }, 1000);
  };

  const keepSubPanelOpen = function() {
    clearTimeout(closeTimers.current['subpanel']);
    clearTimeout(closeTimers.current['more']);
    clearTimeout(closeTimers.current['apps']);
    clearTimeout(closeTimers.current['reports']);
  };

  const handleSaveCustomize = function(newPinned, newOrder) {
    setPinnedApps(newPinned);
    setAppOrder(newOrder);
    setToast(true);
    setTimeout(function() { setToast(false); }, 3000);
  };

  // QBO-style slim item: colored square ONLY on icon, label stays dark
  const renderSlimItem = function(item) {
    const isActive = item.path
      ? (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)))
      : flyout === item.flyout;
    const isHov    = hovItem === item.id;
    const iconColor = isActive || isHov ? '#fff' : DEFAULT;
    const iconBg    = isActive ? '#0E1A1A' : isHov ? '#065F46' : 'transparent';

    return (
      <div
        key={item.id}
        onClick={function(e) {
          if (item.flyout === 'more') { const r = e.currentTarget.getBoundingClientRect(); setMoreAnchorTop(r.top); }
          if (item.flyout === 'feed') { const r = e.currentTarget.getBoundingClientRect(); setFeedAnchorTop(r.top); }
          if (item.flyout === 'reports') { const r = e.currentTarget.getBoundingClientRect(); setReportsAnchorTop(r.top); }
          handleItemClick(item);
        }}
        onMouseEnter={function(e) {
          setHovItem(item.id);
          if (item.flyout) {
            if (item.flyout === 'more') { const r = e.currentTarget.getBoundingClientRect(); setMoreAnchorTop(r.top); }
            if (item.flyout === 'feed') { const r = e.currentTarget.getBoundingClientRect(); setFeedAnchorTop(r.top); }
            if (item.flyout === 'reports') { const r = e.currentTarget.getBoundingClientRect(); setReportsAnchorTop(r.top); }
            startOpen(item.flyout, 300);
          }
        }}
        onMouseLeave={function() {
          setHovItem(null);
          if (item.flyout) startClose(item.flyout);
        }}
        style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'6px 2px', borderRadius:10, cursor:'pointer', width:76, marginBottom:1, background:'transparent', transition:'all 0.15s ease', position:'relative', boxShadow: (item.flyout === 'apps' && flyoutPinned) ? 'inset 3px 0 0 #0F6E56' : 'none' }}
      >
        <div style={{ width:40, height:40, borderRadius:11, display:'flex', alignItems:'center', justifyContent:'center', background:iconBg, transition:'background 120ms' }}>
          {item.id === 'feed'
            ? <AsteriskIcon size={22} color={iconColor}/>
            : item.id === 'customize'
              ? <CustomSlidersIcon size={22} color={iconColor}/>
              : item.icon ? <item.icon size={22} color={iconColor} strokeWidth={2}/> : null
          }
        </div>
        <span style={{ fontSize:11, fontWeight:isActive?600:500, color:DEFAULT, letterSpacing:'0.02em', textAlign:'center', lineHeight:1.3 }}>
          {item.label}
        </span>
      </div>
    );
  };

  const renderAccountingPinned = function() {
    const isActive  = flyout === 'apps' && flyoutExpanded === 'accounting';
    const isHov     = hovItem === 'accounting';
    const iconColor = isActive || isHov ? '#fff' : DEFAULT;
    const iconBg    = isActive ? '#0E1A1A' : isHov ? '#065F46' : 'transparent';
    return (
      <div
        onClick={handleAccountingClick}
        onMouseEnter={function(e) { setHovItem('accounting'); startPreview(ALL_APPS[0], e); }}
        onMouseLeave={function() { setHovItem(null); endPreview(); }}
        style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, padding:'6px 2px', borderRadius:10, cursor:'pointer', width:76, marginBottom:1, background:'transparent', transition:'all 0.15s ease', position:'relative' }}
      >
        <div style={{ width:40, height:40, borderRadius:11, display:'flex', alignItems:'center', justifyContent:'center', background:iconBg, transition:'background 120ms' }}>
          <LayoutDashboard size={22} color={iconColor} strokeWidth={2}/>
        </div>
        <span style={{ fontSize:11, fontWeight:isActive?600:500, color:DEFAULT, textAlign:'center', lineHeight:1.3 }}>Accounting</span>
      </div>
    );
  };

  return (
    <div ref={sidebarRef}>
      <style>{SLIDE_IN}</style>

      {toast && (
        <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', background:'#0F172A', color:'#fff', padding:'10px 20px', borderRadius:10, fontSize:13, fontWeight:500, zIndex:999, boxShadow:'0 8px 24px rgba(0,0,0,0.2)', animation:'fadeIn 0.2s ease' }}>
          App menu updated
        </div>
      )}

      {showCustomize && (
        <CustomizeModal
          onClose={function() { setShowCustomize(false); }}
          pinnedApps={pinnedApps}
          appOrder={appOrder}
          onSave={handleSaveCustomize}
        />
      )}

      {!mobile && preview && (
        <HoverPreviewCard
          app={preview.app}
          anchorTop={preview.top}
          onNavigate={function(path) { goTo(path); setPreview(null); }}
          onMouseEnter={keepPreview}
          onMouseLeave={endPreview}
        />
      )}

      {/* === Desktop sidebar: QBO match === */}
      <aside style={{ position:'relative',
        width:96,
        height:'100%', flexShrink:0,
        background:'#F7F8FA',
        borderRight:'1px solid #E8EDF3',
        display:mobile?'none':'flex', flexDirection:'column', alignItems:'center',
        paddingTop:12, paddingBottom:10,
        zIndex:44, fontFamily:FONT,
        overflow:'visible' }}>
        <div style={{ width:44, height:44, margin:'0 auto 10px', display:'grid', placeItems:'center' }}>
          <img src="/apple-touch-icon.png" alt="Novala" style={{ width:44, height:44, objectFit:'cover', display:'block', borderRadius:'50%' }} />
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%' }}>
          {SLIM_ITEMS.map(function(item) { return renderSlimItem(item); })}
        </div>
        <div style={{ width:'100%', display:'flex', flexDirection:'column', alignItems:'center', padding:'10px 0 4px' }}>
          <div style={{ fontSize:9, fontWeight:600, color:'#475569', letterSpacing:'0.08em', textTransform:'uppercase', textAlign:'center', marginBottom:6 }}>PINNED</div>
          {renderAccountingPinned()}
        </div>
        <div style={{ width:48, height:1, background:'#E8EDF3', margin:'8px 0', flexShrink:0 }}/>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%', marginTop:4 }}>
          {renderSlimItem(SLIM_BOTTOM[0])}
        </div>
        <div style={{ marginTop:20, width:'100%', display:'flex', flexDirection:'column' }}>
          <div style={{ height:1, background:'#E8EDF3', margin:'0 12px 10px', flexShrink:0 }}/>
          <div style={{ width:'calc(100% - 12px)', padding:'4px 2px', background:'#fff', border:'1px solid #E8EDF3', borderRadius:12, display:'flex', flexDirection:'column', alignItems:'center', margin:'0 auto 4px' }}>
            {renderSlimItem(SLIM_BOTTOM[1])}
          </div>
        </div>
      </aside>

      {/* === Mobile drawer === */}
      {mobile && (
        <MobileDrawer
          mobileOpen={mobileOpen}
          onMobileClose={onMobileClose}
          location={location}
          onLogout={onLogout}
          onNavigate={goTo}
        />
      )}

      {/* === Flyout panels (desktop only) === */}
      {!mobile && flyout === 'create' && (
        <CreateFlyout
          flyoutId="create"
          onClose={function() { setFlyout(null); setFlyoutPinned(false); }}
          onNavigate={goTo}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
        />
      )}

      {!mobile && flyout === 'bookmarks' && (
        <FlyoutPanel
          title="Bookmarks"
          flyoutId="bookmarks"
          onClose={function() { setFlyout(null); setFlyoutPinned(false); }}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
          headerAction={<button onClick={function(){}} aria-label="Edit bookmarks" style={{ background:'none', border:'none', cursor:'pointer', color:MUTED, display:'flex', alignItems:'center', padding:2 }}><Pencil size={14}/></button>}
        >
          <div style={{ padding:'20px 20px 14px', color:MUTED, fontSize:13 }}>Add a bookmark to get started.</div>
          <div style={{ height:1, background:'#F1F5F9', margin:'0 16px' }}/>
          <div
            onClick={function() { setFlyout(null); }}
            style={{ padding:'12px 20px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13, fontWeight:600, color:DEFAULT, transition:'all 0.15s' }}
            onMouseEnter={function(e) { e.currentTarget.style.background='#F7F8FA'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background='transparent'; }}
          >
            <span style={{ color:'#0F6E56', fontWeight:800, fontSize:16 }}>+</span>
            Bookmark current page
          </div>
        </FlyoutPanel>
      )}

      {!mobile && flyout === 'feed' && (
        <FeedFlyout
          flyoutId="feed"
          onClose={function() { setFlyout(null); setFlyoutPinned(false); }}
          onNavigate={goTo}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
          anchorTop={feedAnchorTop}
        />
      )}

      {!mobile && flyout === 'reports' && (
        <FlyoutPanel
          title="Reports"
          flyoutId="reports"
          anchorTop={reportsAnchorTop}
          onClose={function() { setFlyout(null); setFlyoutPinned(false); setHoveredReportsGroup(null); }}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
        >
          <div style={{ padding:'8px 12px' }}>
            {REPORTS_ITEMS.map(function(item) {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              return (
                <div
                  key={item.label}
                  onClick={function() { if (!hasChildren && item.path) goTo(item.path); }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, cursor:'pointer', marginBottom:2, transition:'all 0.15s' }}
                  onMouseEnter={function(e) {
                    e.currentTarget.style.background='#F1F5F9';
                    if (hasChildren) {
                      clearTimeout(closeTimers.current['subpanel']);
                      setHoveredReportsGroup(item.label);
                      setHoveredReportsGroupTop(e.currentTarget.getBoundingClientRect().top);
                    }
                  }}
                  onMouseLeave={function(e) {
                    e.currentTarget.style.background='transparent';
                    if (hasChildren) closeSubPanelSoon();
                  }}
                >
                  <span style={{ fontSize:13, color:DEFAULT, fontWeight:500, flex:1 }}>{item.label}</span>
                  <ChevronRight size={13} color="#CBD5E1"/>
                </div>
              );
            })}
          </div>
        </FlyoutPanel>
      )}

      {!mobile && flyout === 'reports' && hoveredReportsGroup && (() => {
        const group = REPORTS_ITEMS.find(function(r) { return r.label === hoveredReportsGroup; });
        if (!group || !group.children) return null;
        return (
          <AppSubPanel
            app={{ id: 'reports-'+hoveredReportsGroup, label: hoveredReportsGroup, items: group.children }}
            onNavigate={goTo}
            onClose={function() { setHoveredReportsGroup(null); setFlyout(null); setFlyoutPinned(false); }}
            onMouseEnter={keepSubPanelOpen}
            onMouseLeave={closeSubPanelSoon}
            anchorTop={hoveredReportsGroupTop}
            anchorLeft={400}
          />
        );
      })()}

      {!mobile && flyout === 'apps' && (
        <AllAppsFlyout
          flyoutId="apps"
          onClose={function() { setFlyout(null); setFlyoutExpanded(null); setFlyoutPinned(false); setHoveredSubApp(null); }}
          onNavigate={goTo}
          expandedId={flyoutExpanded}
          location={location}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
          pinned={flyoutPinned}
          onCategoryHover={openSubPanel}
          onCategoryLeave={closeSubPanelSoon}
        />
      )}

      {!mobile && ((flyout === 'apps' && !flyoutPinned) || flyout === 'more') && hoveredSubApp && (
        <AppSubPanel
          app={ALL_APPS.find(function(a) { return a.id === hoveredSubApp; })}
          onNavigate={goTo}
          onClose={function() { setHoveredSubApp(null); setFlyout(null); }}
          onMouseEnter={keepSubPanelOpen}
          onMouseLeave={closeSubPanelSoon}
          anchorTop={hoveredSubAppTop}
          anchorLeft={flyout === 'more' ? 320 : 406}
        />
      )}

      {!mobile && flyout === 'more' && (
        <FlyoutPanel
          flyoutId="more"
          compact
          anchorTop={moreAnchorTop}
          onClose={function() { setFlyout(null); setFlyoutPinned(false); }}
          onPanelEnter={onPanelEnter}
          onPanelLeave={onPanelLeave}
        >
          <div>
            {ALL_APPS.filter(function(a) { return !pinnedApps.includes(a.id); }).map(function(app) {
              const isHovered = hoveredSubApp === app.id;
              return (
                <div
                  key={app.id}
                  onClick={function() { setFlyout('apps'); setFlyoutExpanded(app.id); setFlyoutPinned(true); }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', cursor:'pointer', transition:'all 0.12s', background: isHovered ? '#F1F5F9' : 'transparent' }}
                  onMouseEnter={function(e) { e.currentTarget.style.background='#F1F5F9'; openSubPanel(app.id, e.currentTarget.getBoundingClientRect().top); }}
                  onMouseLeave={function(e) { e.currentTarget.style.background='transparent'; closeSubPanelSoon(); }}
                >
                  <div style={{ width:28, height:28, borderRadius:'50%', background:'#1E293B', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <FlyServiceIcon id={app.id} color={app.color}/>
                  </div>
                  <span style={{ fontSize:13, color:DEFAULT, fontWeight:500, flex:1 }}>{app.label}</span>
                  <ChevronRight size={12} color="#CBD5E1"/>
                </div>
              );
            })}
          </div>
        </FlyoutPanel>
      )}
    </div>
  );
}
